import React, { useRef, useState } from 'react';
import classNames from 'classnames';

import getCountryCodes from '../../../translations/countryCodes';
import { FormattedMessage } from '../../../util/reactIntl';
import { getShippingDetails as getUserShippingDetails } from '../../../util/userDataExtractor';
import { Heading, InlineTextButton, Modal } from '../../../components';
import ShippingAddressForm from '../../CheckoutPage/ShippingAddressForm/ShippingAddressForm';

import AddressLinkMaybe from './AddressLinkMaybe';

import css from './TransactionPanel.module.css';

const noop = () => null;
const isObject = value => value && typeof value === 'object';

const getFirstValue = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const getAddressField = (address, ...keys) => {
  if (!isObject(address)) {
    return '';
  }

  return getFirstValue(...keys.map(key => address?.[key])) || '';
};

const normalizeCountryCode = country => {
  if (isObject(country)) {
    return country.code || country.value || '';
  }

  return country || '';
};

const hasAddressData = address => {
  if (!isObject(address)) {
    return false;
  }

  return Boolean(
    getAddressField(address, 'line1', 'street1', 'addressLine1') ||
      getAddressField(address, 'line2', 'street2', 'addressLine2') ||
      getAddressField(address, 'city') ||
      getAddressField(address, 'postalCode', 'postal_code', 'zip') ||
      getAddressField(address, 'state') ||
      normalizeCountryCode(getAddressField(address, 'country', 'countryCode'))
  );
};

const getUserId = user => user?.id?.uuid || user?.id || null;

// Matches the server's recovery of an interrupted label purchase (server/api/shippo/shippo.js)
const STALE_LABEL_PENDING_MS = 15 * 60 * 1000;
const isFulfilledAction = action => action?.meta?.requestStatus === 'fulfilled';

const getReadableAddressErrorMessage = error => {
  const details = error?.details || error?.data?.details || null;
  const validationMessages = details?.validationMessages;

  if (Array.isArray(validationMessages) && validationMessages.length > 0) {
    return validationMessages.join(' ');
  }

  const shippoMessage = details?.shippoMessage;
  if (typeof shippoMessage === 'string' && shippoMessage.trim()) {
    return shippoMessage;
  }

  return typeof error?.message === 'string' && error.message.trim() ? error.message : null;
};

const getProfileShippingDetails = ({ customer, currentUser }) => {
  const customerDetails = getUserShippingDetails(customer);
  if (customerDetails) {
    return customerDetails;
  }

  const currentUserIsCustomer =
    getUserId(currentUser) && getUserId(customer) && getUserId(currentUser) === getUserId(customer);

  return currentUserIsCustomer ? getUserShippingDetails(currentUser) : null;
};

const getMetadataShippingDetails = metadata =>
  metadata?.shippingDetails?.shippingDetails || metadata?.shippingDetails || null;

const hasCorrectedMetadataShippingDetails = metadata =>
  Boolean(
    metadata?.shippingDetailsUpdatedAt ||
      metadata?.shippingAddressCorrection ||
      metadata?.shippingAddressObjectId ||
      metadata?.toAddressId
  );

const getShippingInfo = ({ protectedShippingDetails, metadataShippingDetails, protectedData }) =>
  protectedShippingDetails?.moreDetails?.shippingInfo ||
  metadataShippingDetails?.moreDetails?.shippingInfo ||
  protectedData?.shippingDetails?.moreDetails?.shippingInfo ||
  {};

const hasShippoRateSelection = ({ protectedData, shippingInfo }) =>
  Boolean(
    protectedData?.rate ||
      protectedData?.parcelId ||
      protectedData?.carrierAccount ||
      protectedData?.serviceLevel ||
      shippingInfo?.rate ||
      shippingInfo?.parcelId ||
      shippingInfo?.carrierAccount ||
      shippingInfo?.serviceLevel ||
      shippingInfo?.shipmentId
  );

const isFailureTrackingState = trackingInfo => {
  const state = `${trackingInfo?.status || trackingInfo?.object_state || ''}`.toUpperCase();
  return ['ERROR', 'FAILURE', 'FAILED', 'INVALID'].includes(state);
};

const hasUsableTrackingLabel = trackingInfo =>
  !isFailureTrackingState(trackingInfo) &&
  Boolean(
    trackingInfo?.label_url ||
      trackingInfo?.labelUrl ||
      trackingInfo?.tracking_number ||
      trackingInfo?.trackingNumber
  );

const getInitialValuesFromShippingDetails = shippingDetails => {
  const address = shippingDetails?.address || shippingDetails || {};
  return {
    name: shippingDetails?.name || address?.name || '',
    phoneNumber:
      shippingDetails?.phoneNumber ||
      shippingDetails?.phone ||
      address?.phoneNumber ||
      address?.phone ||
      '',
    line1: getAddressField(address, 'line1', 'street1', 'addressLine1'),
    line2: getAddressField(address, 'line2', 'street2', 'addressLine2'),
    city: getAddressField(address, 'city'),
    state: getAddressField(address, 'state'),
    zip: getAddressField(address, 'postalCode', 'postal_code', 'zip'),
    country: normalizeCountryCode(getAddressField(address, 'country', 'countryCode')) || 'US',
  };
};

const buildShippingDetailsFromFormValues = ({ values, existingMoreDetails }) => {
  const postalCode = values?.postalCode || values?.zip || '';
  const shippingDetails = {
    name: values?.name || '',
    phoneNumber: values?.phoneNumber || '',
    address: {
      line1: values?.line1 || '',
      line2: values?.line2 || '',
      city: values?.city || '',
      state: values?.state || '',
      postalCode,
      country: values?.country || 'US',
    },
  };

  return existingMoreDetails
    ? {
        ...shippingDetails,
        moreDetails: existingMoreDetails,
      }
    : shippingDetails;
};

// Functional component as a helper to build ActivityFeed section
const DeliveryInfoMaybe = props => {
  const {
    className,
    rootClassName,
    protectedData,
    listing,
    locale,
    transaction,
    customer,
    currentUser,
    fetchedShippingDetails,
    transactionRole,
    isTutulistSellerShipping = false,
    createShippoLabelInProgress = false,
    createShippoLabelError = null,
    updateShippingAddressInProgress = false,
    updateShippingAddressError = null,
    onRetryShippoLabelWithAddress = noop,
    onRetryShippoLabel = noop,
    onRefreshCurrentUser = noop,
    onManageDisableScrolling = noop,
  } = props;
  const [isCorrectionModalOpen, setCorrectionModalOpen] = useState(false);
  const [addressCorrectionError, setAddressCorrectionError] = useState(null);
  const [addressCorrectionErrorMessage, setAddressCorrectionErrorMessage] = useState(null);
  const [addressCorrectionInProgress, setAddressCorrectionInProgress] = useState(false);
  const [labelCorrectionSucceeded, setLabelCorrectionSucceeded] = useState(false);
  const correctionPending = useRef(false);

  const classes = classNames(rootClassName || css.deliveryInfoContainer, className);
  const metadata = transaction?.attributes?.metadata || {};
  const deliveryMethod =
    protectedData?.deliveryMethod || transaction?.attributes?.protectedData?.deliveryMethod;
  const isShipping = deliveryMethod === 'shipping';
  const isPickup = deliveryMethod === 'pickup';

  const protectedShippingDetails =
    protectedData?.shippingDetails?.shippingDetails || protectedData?.shippingDetails || null;
  const metadataShippingDetails = getMetadataShippingDetails(metadata);
  const correctedMetadataShippingDetails = hasCorrectedMetadataShippingDetails(metadata)
    ? metadataShippingDetails
    : null;
  const profileShippingDetails = getProfileShippingDetails({
    customer: customer || transaction?.customer,
    currentUser,
  });
  const currentShippingDetails =
    correctedMetadataShippingDetails ||
    protectedShippingDetails ||
    metadataShippingDetails ||
    fetchedShippingDetails ||
    profileShippingDetails ||
    {};
  const address =
    [
      currentShippingDetails?.address,
      currentShippingDetails,
      correctedMetadataShippingDetails?.address,
      correctedMetadataShippingDetails,
      protectedData?.address,
      protectedData?.shippingAddress,
      protectedData?.buyerInfo?.address,
      protectedShippingDetails?.address,
      protectedShippingDetails,
      metadata?.shippingAddress,
      metadataShippingDetails?.address,
      metadataShippingDetails,
      fetchedShippingDetails?.address,
      fetchedShippingDetails,
      profileShippingDetails?.address,
      profileShippingDetails,
    ].find(hasAddressData) || {};
  const shippingName =
    getFirstValue(
      currentShippingDetails?.name,
      correctedMetadataShippingDetails?.name,
      protectedData?.name,
      protectedData?.buyerInfo?.name,
      protectedShippingDetails?.name,
      metadataShippingDetails?.name,
      fetchedShippingDetails?.name,
      profileShippingDetails?.name
    ) || '';
  const hasShippingAddressData = hasAddressData(address) || Boolean(shippingName);
  const shouldShowShipping = isShipping || (!deliveryMethod && hasShippingAddressData);
  const shippingInfo = getShippingInfo({
    protectedShippingDetails,
    metadataShippingDetails,
    protectedData,
  });
  const trackingInfo = metadata?.trackingInfo || {};
  const hasRateSelection = hasShippoRateSelection({ protectedData, shippingInfo });
  const hasLabelFailureState = Boolean(
    metadata?.shippoLabelError ||
      createShippoLabelError ||
      isFailureTrackingState(trackingInfo) ||
      (hasRateSelection && !hasUsableTrackingLabel(trackingInfo))
  );
  const isCustomer = transactionRole === 'customer';
  const shouldShowLabelRecovery =
    isCustomer &&
    isTutulistSellerShipping &&
    hasRateSelection &&
    !hasUsableTrackingLabel(trackingInfo) &&
    hasLabelFailureState &&
    !labelCorrectionSucceeded;
  const labelError = createShippoLabelError || metadata?.shippoLabelError;
  // Historical UPS account-country failures were saved as edit_address. Use
  // their exact provider code, unless a newer request has returned another error.
  const legacyUpsAccountFailure =
    !createShippoLabelError &&
    isFailureTrackingState(trackingInfo) &&
    trackingInfo?.messages?.some(
      message =>
        String(message?.source || '').toUpperCase() === 'UPS' && String(message?.code) === '120120'
    );
  const savedErrorAction = legacyUpsAccountFailure
    ? 'contact_support'
    : labelError?.details?.action || labelError?.data?.details?.action || labelError?.action;
  // "Already being processed" only holds while a purchase may still be running. After 15
  // minutes the server checks Shippo and recovers it, so retrying is allowed again.
  const isStalePendingLabel =
    (labelError?.code || labelError?.data?.code) === 'SHIPPING_LABEL_PENDING' &&
    Date.now() - Date.parse(labelError?.updatedAt || '') > STALE_LABEL_PENDING_MS;
  const errorAction =
    savedErrorAction === 'refresh' && isStalePendingLabel ? 'retry' : savedErrorAction;
  // Old records retained the exact provider code in trackingInfo even when the
  // top-level error was generic. Migrate that known code at the display boundary.
  const legacyQrFailure = trackingInfo?.messages?.some(m => m?.code === 'qr_code_unavailable');
  const shouldShowAddressCorrection = shouldShowLabelRecovery && errorAction === 'edit_address';
  const shouldShowLabelRetry = shouldShowLabelRecovery && !shouldShowAddressCorrection;
  const shouldShowSellerLabelError =
    !isCustomer &&
    isTutulistSellerShipping &&
    hasRateSelection &&
    !hasUsableTrackingLabel(trackingInfo) &&
    hasLabelFailureState;
  const correctionInProgress =
    addressCorrectionInProgress || createShippoLabelInProgress || updateShippingAddressInProgress;
  const existingMoreDetails =
    currentShippingDetails?.moreDetails || protectedShippingDetails?.moreDetails || null;

  const handleSubmitAddressCorrection = async values => {
    if (correctionPending.current) return null;
    setAddressCorrectionError(null);
    setAddressCorrectionErrorMessage(null);
    setLabelCorrectionSucceeded(false);

    if (!values?.phoneNumber || values?.invalidPhoneValue) {
      setAddressCorrectionError('phone');
      return null;
    }

    correctionPending.current = true;
    setAddressCorrectionInProgress(true);
    try {
      const shippingDetails = buildShippingDetailsFromFormValues({
        values,
        existingMoreDetails,
      });
      const retryAction = await onRetryShippoLabelWithAddress({
        shippingDetails,
      });

      if (isFulfilledAction(retryAction)) {
        setLabelCorrectionSucceeded(true);
        setCorrectionModalOpen(false);
      } else {
        setAddressCorrectionError('label');
        setAddressCorrectionErrorMessage(getReadableAddressErrorMessage(retryAction?.payload));
      }

      return retryAction;
    } catch (error) {
      setAddressCorrectionError('address');
      setAddressCorrectionErrorMessage(getReadableAddressErrorMessage(error));
      return error;
    } finally {
      correctionPending.current = false;
      setAddressCorrectionInProgress(false);
    }
  };

  const correctionMessage = shouldShowAddressCorrection ? (
    <p className={css.addressError}>
      <FormattedMessage id="TransactionPanel.shippoLabelAddressError" />{' '}
      <InlineTextButton
        className={css.inlineButton}
        onClick={() => {
          setAddressCorrectionError(null);
          setCorrectionModalOpen(true);
        }}
      >
        <FormattedMessage id="TransactionPanel.updateShippingAddressLink" />
      </InlineTextButton>
    </p>
  ) : shouldShowLabelRecovery && errorAction === 'contact_support' ? (
    <p className={css.addressError}>
      <FormattedMessage id="TransactionPanel.shippoLabelConfigurationError" />
    </p>
  ) : shouldShowLabelRetry ? (
    <p className={css.addressError}>
      {legacyQrFailure ? (
        <FormattedMessage id="TransactionPanel.shippoQrUnavailable" />
      ) : labelError?.message && errorAction ? (
        labelError.message
      ) : (
        <FormattedMessage id="TransactionPanel.shippoLabelRetryError" />
      )}{' '}
      <InlineTextButton
        className={css.inlineButton}
        disabled={
          correctionInProgress ||
          ['refresh', 'contact_seller', 'contact_support'].includes(errorAction)
        }
        onClick={onRetryShippoLabel}
      >
        <FormattedMessage id="TransactionPanel.retryShippingLabel" />
      </InlineTextButton>{' '}
      <InlineTextButton
        className={css.inlineButton}
        disabled={correctionInProgress}
        onClick={() => setCorrectionModalOpen(true)}
      >
        <FormattedMessage id="TransactionPanel.updateShippingAddressLink" />
      </InlineTextButton>
    </p>
  ) : labelCorrectionSucceeded ? (
    <p className={css.addressSuccess}>
      <FormattedMessage id="TransactionPanel.shippoLabelRetrySuccess" />
    </p>
  ) : null;

  const sellerLabelErrorMessage = shouldShowSellerLabelError ? (
    <p className={css.addressError}>
      <FormattedMessage
        id={
          errorAction === 'contact_support'
            ? 'TransactionPanel.shippoLabelConfigurationError'
            : 'TransactionPanel.customershippingInfoHeading'
        }
      />
    </p>
  ) : null;

  const correctionModal = (
    <Modal
      id="TransactionShippingAddressCorrectionModal"
      isOpen={isCorrectionModalOpen}
      onClose={() => setCorrectionModalOpen(false)}
      usePortal
      contentClassName={css.addressCorrectionModal}
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <div className={css.addressCorrectionContent}>
        <Heading as="h2" rootClassName={css.addressCorrectionTitle}>
          <FormattedMessage id="TransactionPanel.updateShippingAddressTitle" />
        </Heading>
        <p className={css.addressCorrectionCopy}>
          <FormattedMessage id="TransactionPanel.updateShippingAddressCopy" />
        </p>
        <ShippingAddressForm
          currentUser={currentUser}
          onRefreshCurrentUser={onRefreshCurrentUser}
          onManageDisableScrolling={onManageDisableScrolling}
          initialValues={getInitialValuesFromShippingDetails(currentShippingDetails)}
          onSubmit={handleSubmitAddressCorrection}
          inProgress={correctionInProgress}
          showShippingAddressForm={false}
          setShowShippingAddressForm={noop}
        />
        {addressCorrectionError || updateShippingAddressError || createShippoLabelError ? (
          <p className={css.addressError}>
            {addressCorrectionErrorMessage ? (
              addressCorrectionErrorMessage
            ) : (
              <FormattedMessage
                id={
                  addressCorrectionError === 'phone'
                    ? 'TransactionPanel.updateShippingAddressPhoneError'
                    : addressCorrectionError === 'label' || createShippoLabelError
                    ? 'TransactionPanel.shippoLabelRetryError'
                    : 'TransactionPanel.updateShippingAddressError'
                }
              />
            )}
          </p>
        ) : null}
      </div>
    </Modal>
  );

  if (isPickup) {
    const pickupLocation = listing?.attributes?.publicData?.location || {};
    return (
      <div className={classes}>
        <Heading as="h3" rootClassName={css.sectionHeading}>
          <FormattedMessage id="TransactionPanel.pickupInfoHeading" />
        </Heading>
        <div className={css.pickupInfoContent}>
          <AddressLinkMaybe
            linkRootClassName={css.pickupAddress}
            location={pickupLocation}
            geolocation={listing?.attributes?.geolocation}
            showAddress={true}
          />
        </div>
      </div>
    );
  } else if (shouldShowShipping) {
    const line1 = getAddressField(address, 'line1', 'street1', 'addressLine1');
    const line2 = getAddressField(address, 'line2', 'street2', 'addressLine2');
    const city = getAddressField(address, 'city');
    const postalCode = getAddressField(address, 'postalCode', 'postal_code');
    const zip = getAddressField(address, 'zip');
    const state = getAddressField(address, 'state');
    const countryCode = normalizeCountryCode(getAddressField(address, 'country', 'countryCode'));
    const countryCodes = getCountryCodes(locale);
    const countryInfo = countryCodes.find(c => c.code === countryCode);
    const country = countryInfo?.name || countryCode;
    const resolvedPostalCode = postalCode || zip || '';

    return (
      <div className={classes}>
        {correctionMessage}
        {sellerLabelErrorMessage}
        <Heading as="h3" rootClassName={css.sectionHeading}>
          <FormattedMessage id="TransactionPanel.shippingInfoHeading" />
        </Heading>
        <div className={css.shippingInfoContent}>
          {shippingName}
          <br />
          {line1}
          {line2 ? `, ${line2}` : ''}
          <br />
          {city}
          {city ? ', ' : ''}
          {state ? `${state}, ` : ''}
          {resolvedPostalCode}
          <br />
          {country}
          <br />
        </div>
        {correctionModal}
      </div>
    );
  }
  return null;
};

export default DeliveryInfoMaybe;
